// lib
import { useContext, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/components/context'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import { Checkbox } from 'primereact/checkbox'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'

const Attachments = ({ features, onUpload, onRemove }) => {
  const { sidebarState } = useContext(SidebarDataContext)

  const [failed, setFailed] = useState(sidebarState.attachments.failed || false)
  const [errors, setErrors] = useState(sidebarState.attachments.errors || [])

  const [progress, setProgress] = useState(sidebarState.attachments.progress || 0)
  const [processing, setProcessing] = useState(sidebarState.attachments.processing || false)

  const [reportAssetPath, setReportAssetPath] = useState(window.report_asset_path)
  const [reportAssetFilename, setReportAssetFilename] = useState(window.report_asset_filename)
  const [reportAssetPrettySize, setReportAssetPrettySize] = useState(window.report_asset_pretty_size)

  const [isReportExist, setIsReportExist] = useState(window.report_exists)

  const [isPublic, setIsPublic] = useState(false)

  const inputRef = useRef(null)

  const onAttachmentUpload = () => {
    if (!inputRef.current?.files.length) return
    onUpload(inputRef.current.files[0], isPublic)
  }

  const onAttachmentRemove = () => onRemove()

  useEffect(() => {
    setReportAssetPath(window.report_asset_path)
    setReportAssetFilename(window.report_asset_filename)
    setReportAssetPrettySize(window.report_asset_pretty_size)

    setIsReportExist(window.report_exists)

    setFailed(sidebarState.attachments.failed || false)
    setErrors(sidebarState.attachments.errors || [])
    setProgress(sidebarState.attachments.progress || 0)
    setProcessing(sidebarState.attachments.processing || false)
  }, [sidebarState.attachments])

  return isReportExist ? (
    PageGlobals.CAN_CREATE_ATTACHMENT || (window.report_exists && PageGlobals.CAN_VIEW_ATTACHMENT) ? (
      <div className={style.section}>
        <div className={style.row}>
          <div className="p-fluid p-col-12">
            {PageGlobals.CAN_CREATE_ATTACHMENT ? (
              <span className={style['row-delete']} aria-label="Remove attachment" {...pressable(onAttachmentRemove)}>
                <i className="icon-aytmfd-x-v2" />
              </span>
            ) : null}

            <a
              data-testid="stat-exports-attachments-download"
              className="p-component p-button p-button-rounded p-button-blue"
              href={reportAssetPath}
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon-aytmfd-export-v2" />
              {reportAssetFilename}&nbsp;{reportAssetPrettySize}
            </a>
          </div>
        </div>
      </div>
    ) : null
  ) : PageGlobals.CAN_CREATE_ATTACHMENT ? (
    <>
      <div className={style.section}>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-12`}>
            <Checkbox
              data-testid="stat-exports-attachments-public"
              className="p-checkbox-sm"
              aria-labelledby="stat-exports-attachments-public-label"
              checked={isPublic}
              disabled={processing || (!PageGlobals.CAN_CREATE_ATTACHMENT && features.seal_report.disabled)}
              onChange={
                processing || (!PageGlobals.CAN_CREATE_ATTACHMENT && features.seal_report.disabled)
                  ? null
                  : () => setIsPublic(!isPublic)
              }
            />
            <span
              data-testid="stat-exports-attachments-public-label"
              id="stat-exports-attachments-public-label"
              className={classNames(style['row-label'], style['row-label-sm'], {
                'p-disabled': processing || (!PageGlobals.CAN_CREATE_ATTACHMENT && features.seal_report.disabled)
              })}
              onClick={
                processing || (!PageGlobals.CAN_CREATE_ATTACHMENT && features.seal_report.disabled)
                  ? null
                  : () => setIsPublic(!isPublic)
              }
            >
              Anyone with link can download
            </span>
          </div>
        </div>
      </div>
      <div className={style.section}>
        <div className={`${style.row} ${style['row-upload']}`}>
          {processing ? (
            <span className="p-component p-button p-button-rounded p-button-blue p-disabled">
              Uploading {progress}%
            </span>
          ) : (
            <span className="p-component p-button p-button-rounded p-button-blue">
              <i className="icon-aytmfd-export-v2" />
              <span id="stat-exports-attachments-upload-label">Upload report file</span>
              <input
                ref={inputRef}
                data-testid="stat-exports-attachments-upload"
                aria-labelledby="stat-exports-attachments-upload-label"
                type="file"
                name="file"
                accept="*"
                tabIndex="-1"
                multiple={false}
                onChange={onAttachmentUpload}
              />
            </span>
          )}
        </div>
      </div>
      {failed ? (
        <div className={style.section}>
          {0 < errors.length ? (
            <div
              className={`${style.row} ${style['row-error']}`}
              dangerouslySetInnerHTML={{ __html: errors.join('<br />') }}
            />
          ) : (
            <div className={`${style.row} ${style['row-error']}`}>
              An error occurred while uploading the report file. If the problem is reproducible please contact Support
              team.
            </div>
          )}
        </div>
      ) : null}
    </>
  ) : null
}

Attachments.propTypes = {
  features: PropTypes.object,
  onUpload: PropTypes.func,
  onRemove: PropTypes.func
}

Attachments.displayName = 'Attachments'

export default Attachments
